import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { athleteApi, fileApi, beltApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../contexts/ThemeContext';
// FASE 2 — mostrar a data da graduação em dd/mm/aaaa
import { formatarDataBr } from '../lib/graduacao';
import { 
  ArrowLeft, 
  Loader2, 
  Download,
  Medal,
  Trophy,
  User,
  Calendar,
  Scale,
  Shield,
  Flag,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

export default function AthleteCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [data, setData] = useState(null);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardRes, beltsRes] = await Promise.all([
          athleteApi.getCard(id),
          beltApi.list()
        ]);
        setData(cardRes.data);
        setBelts(beltsRes.data);
      } catch (error) {
        toast.error('Erro ao carregar carteirinha');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </MainLayout>
    );
  }

  if (!data) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <User className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white">Atleta não encontrado</h2>
        </div>
      </MainLayout>
    );
  }

  const { athlete, team, stats } = data;

  const beltInfo = belts.find(b => b.name === athlete.belt);
  const beltColor = beltInfo?.color_code || '#000000';
  const isLightBelt = ['#FFFFFF', '#EAB308', '#9CA3AF', '#F97316'].includes(beltColor);

  // Palette: keeps the card light in light mode, dark in dark mode.
  // Using inline `style` bypasses the global text-white/bg-* overrides.
  const palette = isLight
    ? {
        cardBg: 'linear-gradient(135deg, #FFFFFF 0%, #F4F4F5 100%)',
        cardBorder: 'rgba(184,148,31,0.55)',
        title: '#18181B',
        subtitle: '#52525B',
        muted: '#71717A',
        athleteName: '#18181B',
        innerBox: '#F4F4F5',
        innerBorder: '#E4E4E7',
        innerText: '#18181B',
        innerLabel: '#52525B',
        qrWrap: '#FFFFFF',
        qrBorder: '1px solid #E4E4E7',
        divider: '#E4E4E7',
      }
    : {
        cardBg: 'linear-gradient(135deg, #141414 0%, #0A0A0A 100%)',
        cardBorder: 'rgba(212,175,55,0.3)',
        title: '#FFFFFF',
        subtitle: '#71717A',
        muted: '#71717A',
        athleteName: '#FFFFFF',
        innerBox: '#1A1A1A',
        innerBorder: '#27272A',
        innerText: '#FFFFFF',
        innerLabel: '#71717A',
        qrWrap: '#FFFFFF',
        qrBorder: 'none',
        divider: '#27272A',
      };

  return (
    <MainLayout>
      <div className="escala-original space-y-6" data-testid="athlete-card-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/athletes')}
              className="text-[#A1A1AA] hover:text-white"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white font-['Chivo']">Carteirinha do Atleta</h1>
              <p className="text-[#A1A1AA] mt-1">Documento de identificação</p>
            </div>
          </div>
          <Button
            onClick={handlePrint}
            className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
          >
            <Download size={20} className="mr-2" />
            Imprimir
          </Button>
        </div>

        {/* Card Preview */}
        <div className="flex justify-center">
          <Card
            className="w-full max-w-md overflow-hidden print:shadow-none"
            style={{
              background: palette.cardBg,
              borderColor: palette.cardBorder,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          >
            {/* Header Strip */}
            <div className="h-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B]" />
            
            <CardContent className="p-6">
              {/* Logo and Title */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h2 className="font-bold font-['Chivo']" style={{ color: palette.title }}>BJJ Manager</h2>
                    <p className="text-xs" style={{ color: palette.subtitle }}>Carteira de Atleta</p>
                  </div>
                </div>
                <div className="p-2 rounded-lg" style={{ background: palette.qrWrap, border: palette.qrBorder }}>
                  <QRCodeSVG 
                    value={`ATHLETE:${athlete._id}`} 
                    size={64}
                    level="M"
                  />
                </div>
              </div>

              {/* Athlete Info */}
              <div className="flex gap-4 mb-6">
                {(athlete.photo_url || athlete.photo_path) ? (
                  <img 
                    src={athlete.photo_url || fileApi.getUrl(athlete.photo_path)} 
                    alt={athlete.name}
                    className="w-24 h-24 rounded-xl object-cover"
                    data-testid="athlete-card-photo"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-xl flex items-center justify-center text-3xl font-bold"
                    style={{ background: palette.innerBox, color: '#D4AF37' }}
                  >
                    {athlete.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold" style={{ color: palette.athleteName }}>{athlete.name}</h3>
                  <p className="text-sm mt-1" style={{ color: palette.subtitle }}>{team?.name || 'Sem equipe'}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold"
                      style={{
                        backgroundColor: beltColor,
                        color: isLightBelt ? '#000' : '#fff',
                        border: beltColor === '#000000' ? '1px solid #555' : beltColor === '#FFFFFF' ? '1px solid #999' : 'none'
                      }}
                    >
                      {athlete.belt}
                    </span>
                    <span className="text-xs" style={{ color: palette.muted }}>{athlete.age_class}</span>
                    {/* FASE 2 — desde quando tem a faixa atual */}
                    {athlete.belt_since && (
                      <span className="text-xs" style={{ color: palette.muted }} data-testid="card-belt-since">
                        · desde {formatarDataBr(athlete.belt_since)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <Calendar size={16} />
                    Nascimento
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }}>{athlete.birth_date ? athlete.birth_date.split('-').reverse().join('/') : ''}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <Scale size={16} />
                    Peso
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }}>{athlete.weight} kg</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <User size={16} />
                    Documento
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }}>{athlete.document}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <Shield size={16} />
                    Categoria
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }}>{athlete.sex === 'M' ? 'Masculino' : 'Feminino'}</p>
                </div>
                {/* FASE 1 — Nacionalidade e Naturalidade */}
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <Flag size={16} />
                    Nacionalidade
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }} data-testid="card-nationality">{athlete.nationality || '—'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: palette.innerBox }}>
                  <div className="flex items-center gap-2 text-xs mb-1" style={{ color: palette.innerLabel }}>
                    <MapPin size={16} />
                    Naturalidade
                  </div>
                  <p className="font-medium" style={{ color: palette.innerText }} data-testid="card-birth-place">{athlete.birth_place || '—'}</p>
                </div>
              </div>

              {/* Stats */}
              <div
                className="p-4 rounded-lg"
                style={{ background: palette.innerBox, border: `1px solid ${palette.innerBorder}` }}
              >
                <h4 className="text-[#D4AF37] font-bold text-sm mb-3 flex items-center gap-2">
                  <Trophy size={16} />
                  Histórico
                </h4>
                <div className="flex justify-between">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: palette.innerText }}>{stats.wins}</p>
                    <p className="text-xs" style={{ color: palette.innerLabel }}>Vitórias</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: palette.innerText }}>{stats.losses}</p>
                    <p className="text-xs" style={{ color: palette.innerLabel }}>Derrotas</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <Medal className="w-5 h-5 medalha-ouro" />
                      <span className="text-lg font-bold" style={{ color: palette.innerText }}>{stats.gold}</span>
                    </div>
                    <p className="text-xs" style={{ color: palette.innerLabel }}>Ouro</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <Medal className="w-5 h-5 medalha-prata" />
                      <span className="text-lg font-bold" style={{ color: palette.innerText }}>{stats.silver}</span>
                    </div>
                    <p className="text-xs" style={{ color: palette.innerLabel }}>Prata</p>
                  </div>
                  <div className="text-center flex flex-col items-center">
                    <div className="flex items-center gap-1">
                      <Medal className="w-5 h-5 medalha-bronze" />
                      <span className="text-lg font-bold" style={{ color: palette.innerText }}>{stats.bronze}</span>
                    </div>
                    <p className="text-xs" style={{ color: palette.innerLabel }}>Bronze</p>
                  </div>
                  {(stats.second_bronze || 0) > 0 && (
                    <div className="text-center flex flex-col items-center">
                      <div className="flex items-center gap-1">
                        <Medal className="w-5 h-5 medalha-segundo-bronze" />
                        <span className="text-lg font-bold" style={{ color: palette.innerText }}>{stats.second_bronze}</span>
                      </div>
                      <p className="text-xs" style={{ color: palette.innerLabel }}>2º Bronze</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 text-center" style={{ borderTop: `1px solid ${palette.divider}` }}>
                <p className="text-xs" style={{ color: palette.muted }}>ID: {athlete._id}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
